import React from 'react';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const CANONICAL_ORDER = [
  'Nature',
  'Creativity',
  'Stimulation',
  'Independence',
  'Pleasure',
  'Equality',
  'Reputation',
  'Achievement',
  'Authority',
  'Influence',
  'Safety',
  'Security',
  'Tradition',
  'Conformity',
  'Caring',
  'Dependability',
  'Tolerance',
  'Humility',
  'Duty',
];

interface PersonalValuesRendererProps {
  title: string;
  data: any;
  primaryColor?: string;
}

export default function PersonalValuesRenderer({ title, data, primaryColor = '#3B6FC4' }: PersonalValuesRendererProps) {
  const normalized = normalizeValues(data);
  if (normalized.length === 0) return null;

  const chartData = {
    labels: normalized.map((entry) => entry.name),
    datasets: [
      {
        label: 'Personal Values Index',
        data: normalized.map((entry) => entry.value),
        backgroundColor: 'rgba(59, 111, 196, 0.20)',
        borderColor: primaryColor,
        borderWidth: 2,
        pointBackgroundColor: '#1D4ED8',
        pointBorderColor: '#FFFFFF',
        pointBorderWidth: 1.5,
        pointRadius: 4,
        pointHoverRadius: 6,
        fill: true,
      },
    ],
  };

  const maxValue = Math.max(200, Math.max(...normalized.map((entry) => entry.value)) * 1.1);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#0F172A',
        borderColor: '#1E293B',
        borderWidth: 1,
        titleColor: '#F8FAFC',
        bodyColor: '#F1F5F9',
      },
    },
    scales: {
      r: {
        beginAtZero: true,
        max: maxValue,
        startAngle: -Math.PI / 2,
        ticks: {
          display: false,
        },
        grid: {
          color: '#CBD5F5',
        },
        angleLines: {
          color: '#E2E8F0',
          lineWidth: 1,
        },
        pointLabels: {
          color: '#1F2937',
          font: {
            size: 12,
            weight: '500',
            family: 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
          },
        },
      },
    },
  } as const;

  const topThree = [...normalized]
    .sort((a, b) => b.value - a.value)
    .slice(0, 3)
    .filter((entry) => entry.value > 0);

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-2xl font-bold text-slate-900">
            {title?.toLowerCase().includes('personal') ? 'Personal Values' : title}
          </h3>
          <p className="text-sm text-slate-500">
            BASELINE: Online Adult Population | CONTAINS: Engaged Audiences Data
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="text-sm text-slate-600 mb-2">BY INDEX</div>
          <div className="text-xs text-slate-500 mb-4">Baseline index of 100: Online Adult Population</div>
          <div className="h-96 relative">
            <Radar data={chartData} options={options} />
            <QuadrantLabels />
          </div>
        </div>

        <div>
          <h4 className="text-lg font-semibold text-slate-900 mb-4">Top Personal Values</h4>
          <div className="space-y-6">
            {topThree.map((entry, index) => (
              <div key={entry.name} className="border-l-4 pl-4" style={{ borderColor: primaryColor }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl font-bold" style={{ color: primaryColor }}>
                    {entry.value}
                  </span>
                  <span className="text-sm text-slate-500">#{index + 1}</span>
                </div>
                <h5 className="font-semibold text-slate-900 mb-2">{entry.name}</h5>
                <p className="text-sm text-slate-600">{valueDescription(entry.name)}</p>
                <p className="text-xs text-slate-500 mt-1">
                  <span className="font-medium">Tip:</span> {valueTip(entry.name)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function normalizeValues(raw: any): Array<{ name: string; value: number }> {
  const lookup = new Map<string, number>();

  const ingest = (item: any) => {
    if (!item) return;
    if (Array.isArray(item)) {
      item.forEach(ingest);
      return;
    }
    if (typeof item === 'object') {
      const name = item.name || item.label || item.title;
      const valueCandidate =
        item.value ?? item.index ?? item.score ?? item.amount ?? item.percentage;
      const numeric = sanitizeNumber(valueCandidate);
      if (name && numeric > 0) {
        const key = name.toString().trim().toLowerCase();
        const existing = lookup.get(key) ?? 0;
        lookup.set(key, Math.max(existing, numeric));
      }
      Object.values(item).forEach(ingest);
      return;
    }
    if (typeof item === 'string') {
      try {
        const parsed = JSON.parse(item);
        ingest(parsed);
      } catch {
        /* noop */
      }
    }
  };

  ingest(raw);

  return CANONICAL_ORDER.map((label) => {
    const key = label.toLowerCase();
    const value = lookup.get(key) ?? 0;
    return { name: label, value };
  });
}

function sanitizeNumber(value: any): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const numeric = Number(value.replace(/[^0-9.\-]/g, ''));
    return Number.isFinite(numeric) ? numeric : 0;
  }
  return 0;
}

function QuadrantLabels() {
  return (
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute top-4 right-4 space-y-1 text-xs tracking-wide text-slate-500">
        <div className="uppercase">Openness to Change</div>
        <div className="uppercase">Self-Transcendence</div>
        <div className="uppercase">Self-Enhancement</div>
        <div className="uppercase">Conservation</div>
      </div>
    </div>
  );
}

function valueDescription(name: string): string {
  const map: Record<string, string> = {
    Nature: 'Values preservation of the natural environment and sustainability.',
    Creativity: 'Thrives on individuality, imagination, and fresh ideas.',
    Stimulation: 'Seeks excitement, novelty, and adventurous experiences.',
    Independence: 'Prefers self-direction and personal control of outcomes.',
    Pleasure: 'Motivated by enjoyment, gratification, and positive experiences.',
    Equality: 'Believes in fairness, empathy, and equal treatment for all.',
    Reputation: 'Cares about public image, status, and being seen positively.',
    Achievement: 'Strives for accomplishment, progress, and being the best.',
    Authority: 'Responds to leadership, influence, and control.',
    Influence: 'Desires impact, persuasion, and shaping the decisions of others.',
    Safety: 'Focuses on protection, security, and stability in life.',
    Security: 'Seeks predictability, comfort, and reliable surroundings.',
    Tradition: 'Prefers familiar, time-tested practices and cultural roots.',
    Conformity: 'Values harmony, social expectations, and restraint.',
    Caring: 'Guided by empathy, community, and helping others.',
    Dependability: 'Prioritizes reliability, consistency, and trustworthiness.',
    Tolerance: 'Open to different viewpoints, backgrounds, and lifestyles.',
    Humility: 'Favours modesty, authenticity, and avoiding overstatement.',
    Duty: 'Feels a sense of obligation, responsibility, and follow-through.',
  };
  return map[name] || 'Core value guiding decision-making and preferences.';
}

function valueTip(name: string): string {
  const map: Record<string, string> = {
    Nature: 'Highlight sustainability, environmental partnerships, and conservation wins.',
    Creativity: 'Showcase originality, design thinking, and clever problem solving.',
    Stimulation: 'Offer energetic experiences, novelty, and excitement in messaging.',
    Independence: 'Emphasize freedom of choice, flexible options, and self-direction.',
    Pleasure: 'Lean into enjoyment and the positive feelings associated with the brand.',
    Equality: 'Elevate inclusive policies, equal outcomes, and empathy-led storytelling.',
    Reputation: 'Underscore community impact, awards, and responsible behaviour.',
    Achievement: 'Demonstrate clear wins, success stories, and ambitious goals.',
    Authority: 'Feature confident leadership, expertise, and authoritative guidance.',
    Influence: 'Show social proof, endorsements, and opportunities to make an impact.',
    Safety: 'Reassure with protection, safeguards, and precautionary steps.',
    Security: 'Promote reliability, stability, and long-term commitment.',
    Tradition: 'Reference heritage, rituals, and the comfort of what’s familiar.',
    Conformity: 'Position messaging around harmony, respect, and doing the right thing.',
    Caring: 'Tell stories about community, generosity, and lifting others up.',
    Dependability: 'Prove the brand is steady, trustworthy, and always on time.',
    Tolerance: 'Spotlight open-minded culture and the breadth of perspectives served.',
    Humility: 'Keep tone grounded, sincere, and customer-first.',
    Duty: 'Frame offers as a dependable partner in fulfilling obligations.',
  };
  return map[name] || 'Connect messaging to real-world expressions of this value.';
}
